import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { useEffect, useState } from "react";
import { type PaymentTerm, listPaymentTerms } from "@/lib/api";
import { useOrganization } from "@/lib/organization";

export function usePaymentTerms() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "";
  const legalEntityId = scope?.legal_entity_id ?? "";
  const [terms, setTerms] = useState<PaymentTerm[]>([]);
  const [loadedScopeKey, setLoadedScopeKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scopeKey = `${organizationId}:${legalEntityId}`;

  useEffect(() => {
    let cancelled = false;
    setTerms([]);
    setLoadedScopeKey("");
    setError(null);
    if (!organizationId || !legalEntityId) return;
    listPaymentTerms()
      .then((rows) => {
        if (!cancelled) {
          setTerms(rows);
          setLoadedScopeKey(scopeKey);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, organizationId, legalEntityId, scopeKey]);

  return { terms: scopeKey && loadedScopeKey === scopeKey ? terms : [], error };
}

function describeTerm(term: PaymentTerm): string {
  const schedule =
    term.kind === "standard"
      ? `Net ${term.due_days ?? "—"}`
      : `Day ${term.day_of_month_due ?? "—"} (next month after ${term.due_next_month_days ?? "—"} days)`;
  return `${term.name} — ${schedule}${term.active ? "" : " (inactive — historical only)"}`;
}

export function PaymentTermPicker({
  id,
  value,
  onChange,
  terms,
  includeContactDefault = false,
  includeNoTerm = true,
  includeInactiveId,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  terms: PaymentTerm[];
  includeContactDefault?: boolean;
  includeNoTerm?: boolean;
  includeInactiveId?: string | null;
  disabled?: boolean;
}) {
  const options = [
    ...(includeNoTerm
      ? [
          {
            value: "none",
            label: includeContactDefault
              ? "Use contact default term"
              : "No default term (enter a due date)",
          },
        ]
      : []),
    ...terms
      .filter((term) => term.active || term.id === includeInactiveId)
      .map((term) => ({ value: term.id, label: describeTerm(term) })),
  ];
  return (
    <Select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={
        includeContactDefault
          ? "Use contact profile default"
          : "Choose a payment term…"
      }
      options={options}
    />
  );
}
